import { TOKEN_STORAGE_KEYS } from './constants';
import { isTokenExpired } from './token';

const ACCESS_TOKEN_CHANGED_EVENT = 'tkm:access-token-changed';

/** App paths that require a valid bearer token before React hydrates. */
export const PROTECTED_ROUTE_PREFIXES = [
  '/student/',
  '/admin/',
  '/teacher/',
  '/parent/',
  '/dev/',
  '/profile',
] as const;

export type AccessTokenValidity = {
  present: boolean;
  valid: boolean;
  expired: boolean;
};

export function isProtectedAppPath(pathname: string): boolean {
  if (!pathname || pathname === '/') return false;
  if (pathname === '/login' || pathname.startsWith('/login/')) return false;
  return PROTECTED_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix.replace(/\/$/, '') || pathname.startsWith(prefix),
  );
}

export function readAccessTokenValidity(): AccessTokenValidity {
  let token: string | null = null;
  let refresh: string | null = null;
  try {
    token = localStorage.getItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
    refresh = localStorage.getItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN);
  } catch {
    return { present: false, valid: false, expired: false };
  }

  if (!token) {
    // Refresh-only: session is recoverable via /auth/refresh (do not treat as logged out).
    if (refresh) {
      return { present: true, valid: true, expired: true };
    }
    return { present: false, valid: false, expired: false };
  }

  const expired = isTokenExpired(token);
  // Access JWT may expire during long live sessions; refresh token keeps the session valid.
  if (expired && refresh) {
    return { present: true, valid: true, expired: true };
  }
  return { present: true, valid: !expired, expired };
}

export function hasValidAccessToken(): boolean {
  return readAccessTokenValidity().valid;
}

let accessTokenRevision = 0;

export function getAccessTokenRevision(): number {
  return accessTokenRevision;
}

export function notifyAccessTokenStateChanged(): void {
  if (typeof window === 'undefined') return;
  accessTokenRevision += 1;
  window.dispatchEvent(new Event(ACCESS_TOKEN_CHANGED_EVENT));
}

export function subscribeAccessTokenState(onStoreChange: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const onCustom = () => onStoreChange();
  const onStorage = (event: StorageEvent) => {
    if (event.key == null || event.key === TOKEN_STORAGE_KEYS.ACCESS_TOKEN) {
      accessTokenRevision += 1;
      onStoreChange();
    }
  };

  window.addEventListener(ACCESS_TOKEN_CHANGED_EVENT, onCustom);
  window.addEventListener('storage', onStorage);

  return () => {
    window.removeEventListener(ACCESS_TOKEN_CHANGED_EVENT, onCustom);
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * Detect same-tab localStorage mutations (devtools, tests) that bypass setTokens/clearTokens.
 * Runs once at app bootstrap.
 */
export function installAccessTokenStorageSync(): void {
  if (typeof window === 'undefined') return;

  const storage = window.localStorage;
  const accessKey = TOKEN_STORAGE_KEYS.ACCESS_TOKEN;
  const refreshKey = TOKEN_STORAGE_KEYS.REFRESH_TOKEN;

  const wrap = <T extends (...args: never[]) => unknown>(method: T, shouldNotify: (...args: Parameters<T>) => boolean): T => {
    const original = method.bind(storage) as T;
    return ((...args: Parameters<T>) => {
      const result = original(...args);
      if (shouldNotify(...args)) {
        notifyAccessTokenStateChanged();
      }
      return result;
    }) as T;
  };

  try {
    storage.setItem = wrap(storage.setItem, (key) => key === accessKey || key === refreshKey);
    storage.removeItem = wrap(storage.removeItem, (key) => key == null || key === accessKey || key === refreshKey);
    storage.clear = wrap(storage.clear, () => true);
  } catch {
    // Some environments block localStorage patching — RequireAuth still guards on navigation.
  }
}
